import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API_BASE } from "../lib/api";
import { CheckCircle, XCircle, Certificate } from "@phosphor-icons/react";

export default function VerifyCertificate() {
  const { cid } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/certificates/verify/${cid}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [cid]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-zinc-500">Verifying…</div>;

  const valid = data?.valid;
  const cert = data?.cert;

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-zinc-200">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center">
          <Link to="/" className="font-display text-xl font-bold tracking-tight">EXAM<span className="text-blue-600">/</span>OS</Link>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-16">
        <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-3 font-semibold">Certificate Verification</div>
        <h1 className="font-display text-4xl font-bold tracking-tight mb-10">{cid}</h1>

        <div className="border border-zinc-200 mb-8" data-testid="verify-result">
          <div className={`p-6 border-b border-zinc-200 flex items-center gap-4 ${valid ? "bg-green-50" : "bg-red-50"}`}>
            {valid ? (
              <>
                <CheckCircle size={36} weight="fill" className="text-green-600" />
                <div>
                  <div className="font-display text-xl font-bold text-green-800">Valid Certificate</div>
                  <div className="text-sm text-green-700">This credential is authentic and issued by EXAM/OS.</div>
                </div>
              </>
            ) : (
              <>
                <XCircle size={36} weight="fill" className="text-red-600" />
                <div>
                  <div className="font-display text-xl font-bold text-red-800">Invalid Certificate</div>
                  <div className="text-sm text-red-700">{data?.message || "Could not verify this certificate."}</div>
                </div>
              </>
            )}
          </div>

          {valid && cert && (
            <div className="grid md:grid-cols-2 gap-0">
              <Cell label="Holder" value={cert.student_name} />
              <Cell label="Exam" value={cert.exam_title} />
              <Cell label="Score" value={`${cert.score} / ${cert.total_marks} (${cert.percentage}%)`} />
              <Cell label="Issued" value={cert.issued_at ? new Date(cert.issued_at).toLocaleDateString() : "—"} />
              <Cell label="Certificate ID" value={cert.cert_id} mono />
              <div className="p-5">
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2 font-semibold">Download</div>
                <a
                  href={`${API_BASE}/certificates/${cert.cert_id}/download`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 bg-zinc-900 text-white px-4 py-2 text-sm hover:bg-zinc-800"
                  data-testid="download-cert-btn"
                >
                  <Certificate size={14} /> Download PDF
                </a>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function Cell({ label, value, mono }) {
  return (
    <div className="p-5 border-b border-r border-zinc-200">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1 font-semibold">{label}</div>
      <div className={`font-medium ${mono ? "font-mono text-sm" : ""}`}>{value}</div>
    </div>
  );
}
